import { BLOCK_SIZE, FLAG, MATERIALS, WORLD } from '../constants.js'
import { airFrictionToDamping, PhysicsWorld } from './world.js'

// One physical setup shared by the builder, the destroyer and the headless
// physics bench, so a build behaves the same everywhere.

export const DEFAULT_GRAVITY = -1.64
export const DEFAULT_TIME_SCALE = 0.34
export const GROUND_FRICTION = 0.9
export const FLAG_DENSITY = 0.006
export const FLAG_FRICTION = 0.8
const BLOCK_RESTITUTION_CAP = 0.05
const BLOCK_ANGULAR_DAMPING = 0.4
const DESTROYER_FLOOR_WIDTH = 96
const DESTROYER_FLOOR_THICKNESS = 2.2
const BUILDER_FLOOR_THICKNESS = 1.35
const BUILDER_WALL_THICKNESS = 1.0

export function getMaterialProfile(material) {
    return MATERIALS[material] || MATERIALS.wood
}

export function getBlockBodyProps(materialKey) {
    const material = getMaterialProfile(materialKey)

    return {
        density: positiveOr(material.density, MATERIALS.wood.density),
        friction: positiveOr(material.friction, 0.6),
        restitution: Math.min(BLOCK_RESTITUTION_CAP, Math.max(0, Number(material.restitution) || 0)),
        linearDamping: airFrictionToDamping(material.frictionAir),
        angularDamping: BLOCK_ANGULAR_DAMPING
    }
}

// Bounce for a contact: projectiles use the target material's restitution
// (or their own ground bounce), structure-on-structure contacts take the
// lower value so stacks stay quiet.
export function resolveStageRestitution(bodyA, bodyB) {
    const bulletA = bodyA.plugin.game?.type === 'bullet'
    const bulletB = bodyB.plugin.game?.type === 'bullet'

    if (bulletA || bulletB) {
        const bullet = bulletA ? bodyA : bodyB
        const other = bulletA ? bodyB : bodyA
        const otherGame = other.plugin.game

        if (otherGame?.type === 'block' || otherGame?.type === 'builder-block') {
            return Number(getMaterialProfile(otherGame.material).restitution) || 0
        }

        if (otherGame?.type === 'flag') {
            return Number(MATERIALS.wood.restitution) || 0
        }

        return Number(bullet.plugin.game?.groundBounce) || 0
    }

    return Math.min(getBodyRestitution(bodyA), getBodyRestitution(bodyB))
}

export function createStageWorld({ gravity = DEFAULT_GRAVITY, timeScale = DEFAULT_TIME_SCALE, onContact = null } = {}) {
    return new PhysicsWorld({
        gravity,
        timeScale,
        onContact,
        resolveRestitution: resolveStageRestitution
    })
}

export function addDestroyerBounds(world) {
    const ground = world.addBox({
        x: 0,
        y: WORLD.groundY - DESTROYER_FLOOR_THICKNESS / 2,
        width: DESTROYER_FLOOR_WIDTH,
        height: DESTROYER_FLOOR_THICKNESS,
        isStatic: true,
        friction: GROUND_FRICTION,
        label: 'ground'
    })
    const leftWall = world.addBox({ x: -24, y: 0, width: 0.7, height: 30, isStatic: true, label: 'wall' })
    const rightWall = world.addBox({ x: 24, y: 0, width: 0.7, height: 30, isStatic: true, label: 'wall' })

    return { ground, walls: [leftWall, rightWall] }
}

export function addBuilderBounds(world, floorTop = WORLD.buildMinY) {
    const buildCenterX = (WORLD.buildMinX + WORLD.buildMaxX) / 2
    const buildCenterY = (WORLD.buildMinY + WORLD.buildMaxY) / 2
    const buildWidth = WORLD.buildMaxX - WORLD.buildMinX
    const buildHeight = WORLD.buildMaxY - WORLD.buildMinY
    const ground = world.addBox({
        x: buildCenterX,
        y: floorTop - BUILDER_FLOOR_THICKNESS / 2,
        width: buildWidth + BUILDER_WALL_THICKNESS * 2,
        height: BUILDER_FLOOR_THICKNESS,
        isStatic: true,
        friction: GROUND_FRICTION,
        label: 'builder-ground'
    })
    const leftWall = world.addBox({
        x: WORLD.buildMinX - BUILDER_WALL_THICKNESS / 2,
        y: buildCenterY,
        width: BUILDER_WALL_THICKNESS,
        height: buildHeight + BUILDER_FLOOR_THICKNESS * 2,
        isStatic: true,
        friction: GROUND_FRICTION,
        label: 'builder-left-wall'
    })
    const rightWall = world.addBox({
        x: WORLD.buildMaxX + BUILDER_WALL_THICKNESS / 2,
        y: buildCenterY,
        width: BUILDER_WALL_THICKNESS,
        height: buildHeight + BUILDER_FLOOR_THICKNESS * 2,
        isStatic: true,
        friction: GROUND_FRICTION,
        label: 'builder-right-wall'
    })

    return { ground, walls: [leftWall, rightWall] }
}

// block: { id, material, shape, x, y, width, height, rotation }
export function addBlockBody(world, block, { type = 'block', awake = true, game = {} } = {}) {
    const props = getBlockBodyProps(block.material)

    return world.addBox({
        x: block.x,
        y: block.y,
        width: block.width || BLOCK_SIZE.width,
        height: block.height || BLOCK_SIZE.height,
        angle: block.rotation || 0,
        ...props,
        awake,
        label: `${type}:${block.id}`,
        game: {
            id: block.id,
            type,
            material: block.material,
            shape: block.shape,
            width: block.width || BLOCK_SIZE.width,
            height: block.height || BLOCK_SIZE.height,
            ...game
        }
    })
}

export function addFlagBody(world, flag, { id = 'flag', type = 'flag', awake = true, game = {} } = {}) {
    return world.addBox({
        x: flag.x,
        y: flag.y,
        width: FLAG.width,
        height: FLAG.height,
        density: FLAG_DENSITY,
        friction: FLAG_FRICTION,
        restitution: 0,
        angularDamping: BLOCK_ANGULAR_DAMPING,
        awake,
        label: id,
        game: { id, type, ...game }
    })
}

function getBodyRestitution(body) {
    const game = body.plugin.game
    if (game?.type === 'block' || game?.type === 'builder-block') {
        return getBlockBodyProps(game.material).restitution
    }

    return 0
}

function positiveOr(value, fallback) {
    const number = Number(value)
    return Number.isFinite(number) && number > 0 ? number : fallback
}
